import * as THREE from "three";
import { CHUNK_HEIGHT, CHUNK_SIZE } from "../config";
import {
  Block,
  FACE_DIRS,
  blockDef,
  faceOccludes,
  isLeaf,
  isWater,
  tileForFace,
  type FaceName,
} from "./blocks";
import { biomeDef } from "../gen/biomes";
import type { Chunk } from "./chunk";
import { AO_CORNERS, faceCorners } from "./faces";
import type { World } from "./world";
import { ATLAS_COLS, ATLAS_ROWS } from "./textures";

const FACE_SHADE: Record<FaceName, number> = {
  py: 1,
  ny: 0.52,
  px: 0.78,
  nx: 0.7,
  pz: 0.86,
  nz: 0.64,
};

const AO_CURVE = [0.48, 0.68, 0.84, 1];

interface BuildBuf {
  pos: number[];
  nrm: number[];
  uv: number[];
  col: number[];
  idx: number[];
}

function buf(): BuildBuf {
  return { pos: [], nrm: [], uv: [], col: [], idx: [] };
}

function pushQuad(
  b: BuildBuf,
  corners: number[][],
  normal: [number, number, number],
  tile: number,
  colors: [number, number, number][],
): void {
  const base = b.pos.length / 3;
  const col = tile % ATLAS_COLS;
  const row = Math.floor(tile / ATLAS_COLS);
  const inset = 0.002;
  const u0 = col / ATLAS_COLS + inset;
  const v1 = 1 - row / ATLAS_ROWS - inset;
  const u1 = (col + 1) / ATLAS_COLS - inset;
  const v0 = 1 - (row + 1) / ATLAS_ROWS + inset;
  const uvs = [
    [u0, v0],
    [u1, v0],
    [u1, v1],
    [u0, v1],
  ];
  for (let i = 0; i < 4; i++) {
    const p = corners[i]!;
    b.pos.push(p[0]!, p[1]!, p[2]!);
    b.nrm.push(normal[0], normal[1], normal[2]);
    b.uv.push(uvs[i]![0]!, uvs[i]![1]!);
    const c = colors[i]!;
    b.col.push(c[0], c[1], c[2]);
  }
  b.idx.push(base, base + 1, base + 2, base, base + 2, base + 3);
}

function sampleWorld(world: World, chunk: Chunk, x: number, y: number, z: number): number {
  if (y < 0 || y >= CHUNK_HEIGHT) return Block.AIR;
  if (x >= 0 && z >= 0 && x < CHUNK_SIZE && z < CHUNK_SIZE) return chunk.get(x, y, z);
  const wx = chunk.cx * CHUNK_SIZE + x;
  const wz = chunk.cz * CHUNK_SIZE + z;
  return world.getBlock(wx, y, wz);
}

function solidish(id: number): boolean {
  return faceOccludes(id);
}

function vertexAO(
  world: World,
  chunk: Chunk,
  sx: number,
  sy: number,
  sz: number,
  dx1: number,
  dy1: number,
  dz1: number,
  dx2: number,
  dy2: number,
  dz2: number,
): number {
  const s1 = solidish(sampleWorld(world, chunk, sx + dx1, sy + dy1, sz + dz1)) ? 1 : 0;
  const s2 = solidish(sampleWorld(world, chunk, sx + dx2, sy + dy2, sz + dz2)) ? 1 : 0;
  const c = solidish(sampleWorld(world, chunk, sx + dx1 + dx2, sy + dy1 + dy2, sz + dz1 + dz2))
    ? 1
    : 0;
  const ao = s1 && s2 ? 0 : 3 - (s1 + s2 + c);
  return AO_CURVE[ao] ?? 1;
}

function tintFor(id: number, biome: number): [number, number, number] {
  const def = blockDef(id);
  const b = biomeDef(biome);
  if (def.tintGrass) return [b.grass[0] / 255, b.grass[1] / 255, b.grass[2] / 255];
  if (def.tintLeaves) return [b.leaves[0] / 255, b.leaves[1] / 255, b.leaves[2] / 255];
  return [1, 1, 1];
}

function shouldDrawFace(id: number, neighbor: number): boolean {
  if (isWater(id)) {
    if (isWater(neighbor)) return false;
    return !faceOccludes(neighbor);
  }
  if (faceOccludes(neighbor)) return false;
  if (isWater(neighbor)) return true;
  if (id === neighbor && isLeaf(id)) return false;
  if (blockDef(id).opaque && !blockDef(neighbor).opaque) return true;
  if (!blockDef(id).opaque && neighbor === Block.AIR) return true;
  if (!blockDef(id).opaque && blockDef(neighbor).plant) return true;
  if (!blockDef(id).opaque && isWater(neighbor)) return true;
  if (!blockDef(id).opaque && neighbor !== id && !faceOccludes(neighbor)) return true;
  return false;
}

function geometryOf(b: BuildBuf): THREE.BufferGeometry | null {
  if (b.idx.length === 0) return null;
  const g = new THREE.BufferGeometry();
  g.setAttribute("position", new THREE.Float32BufferAttribute(b.pos, 3));
  g.setAttribute("normal", new THREE.Float32BufferAttribute(b.nrm, 3));
  g.setAttribute("uv", new THREE.Float32BufferAttribute(b.uv, 2));
  g.setAttribute("color", new THREE.Float32BufferAttribute(b.col, 3));
  g.setIndex(b.idx);
  g.computeBoundingSphere();
  return g;
}

export interface ChunkMeshes {
  solid: THREE.BufferGeometry | null;
  cutout: THREE.BufferGeometry | null;
  water: THREE.BufferGeometry | null;
}

export function meshChunk(world: World, chunk: Chunk): ChunkMeshes {
  const solid = buf();
  const cutout = buf();
  const water = buf();

  for (let y = 0; y < CHUNK_HEIGHT; y++) {
    for (let z = 0; z < CHUNK_SIZE; z++) {
      for (let x = 0; x < CHUNK_SIZE; x++) {
        const id = chunk.get(x, y, z);
        if (id === Block.AIR) continue;
        const def = blockDef(id);
        if (def.mesh === "none") continue;
        const biome = chunk.biomeAt(x, z);
        const tint = tintFor(id, biome);

        if (def.mesh === "cross") {
          const tile = def.tile.side;
          const shade = 0.92;
          const col: [number, number, number] = [tint[0] * shade, tint[1] * shade, tint[2] * shade];
          const colors = [col, col, col, col];
          const inset = 0.15;
          pushQuad(
            cutout,
            [
              [x + inset, y, z + inset],
              [x + 1 - inset, y, z + 1 - inset],
              [x + 1 - inset, y + 1, z + 1 - inset],
              [x + inset, y + 1, z + inset],
            ],
            [0.5, 0, 0.5],
            tile,
            colors,
          );
          pushQuad(
            cutout,
            [
              [x + 1 - inset, y, z + inset],
              [x + inset, y, z + 1 - inset],
              [x + inset, y + 1, z + 1 - inset],
              [x + 1 - inset, y + 1, z + inset],
            ],
            [-0.5, 0, 0.5],
            tile,
            colors,
          );
          continue;
        }

        const target = isWater(id) ? water : def.opaque ? solid : cutout;

        for (const face of FACE_DIRS) {
          const nid = sampleWorld(world, chunk, x + face.dx, y + face.dy, z + face.dz);
          if (!shouldDrawFace(id, nid)) continue;
          if (isWater(id) && isWater(nid)) continue;

          let corners = faceCorners(x, y, z, face.name);
          if (isWater(id) && face.name === "py") {
            corners = corners.map((p) => [p[0]!, p[1]! - 0.12, p[2]!]);
          }
          const tile = tileForFace(id, face.name);
          const shade = FACE_SHADE[face.name];
          const aoSet = AO_CORNERS[face.name];
          const colors: [number, number, number][] = [];
          for (let i = 0; i < 4; i++) {
            const pair = aoSet[i]!;
            const a = pair[0]!;
            const b = pair[1]!;
            const ao = vertexAO(world, chunk, x, y, z, a[0]!, a[1]!, a[2]!, b[0]!, b[1]!, b[2]!);
            const s = shade * ao;
            colors.push([tint[0] * s, tint[1] * s, tint[2] * s]);
          }
          const n: [number, number, number] = [face.dx, face.dy, face.dz];
          pushQuad(target, corners, n, tile, colors);
        }
      }
    }
  }

  return {
    solid: geometryOf(solid),
    cutout: geometryOf(cutout),
    water: geometryOf(water),
  };
}
